import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Scale, Search, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AppShell from "@/components/platform/AppShell";
import { EmptyState, PageTitle } from "@/components/platform/Badges";
import { RightsKpis, StagePipeline } from "@/components/rights/RightsKpis";
import { DueLabel, StageBadge, TypeBadge, VerifyBadge } from "@/components/rights/RightsBadges";
import { NewRequestDialog } from "@/components/rights/NewRequestDialog";
import { RightsSettingsDialog } from "@/components/rights/RightsSettingsDialog";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtD } from "@/lib/platform";
import { useRightsMeta } from "@/lib/rights";

const ALL = "__all";

const Filter = ({ value, onChange, placeholder, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-9 w-[170px] text-xs bg-white" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      <SelectItem value={ALL}>{placeholder}</SelectItem>
      {options.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

const Row = ({ r, onOpen }) => (
  <tr onClick={onOpen} className="border-b border-slate-50 hover:bg-slate-50/80 cursor-pointer transition-colors" data-testid={`rights-row-${r.id}`}>
    <td className="px-4 py-3 font-mono text-xs text-slate-500">{r.request_id}</td>
    <td className="px-4 py-3"><TypeBadge type={r.request_type} label={r.request_type_label} /></td>
    <td className="px-4 py-3">
      <div className="font-medium text-[#003366]">{r.subject_name}</div>
      <div className="text-[11px] text-slate-500">{r.subject_type_label} · <span className="font-mono">{r.linked_person_key}</span></div>
    </td>
    <td className="px-4 py-3 text-xs font-mono text-slate-600">{fmtD(r.received_date)}</td>
    <td className="px-4 py-3">
      <div className="text-xs font-mono text-slate-600">{fmtD(r.target_date)}</div>
      <DueLabel r={r} testId={`rights-due-${r.id}`} />
    </td>
    <td className="px-4 py-3 text-xs text-slate-700">{r.owner}</td>
    <td className="px-4 py-3"><VerifyBadge status={r.verification_status} /></td>
    <td className="px-4 py-3"><StageBadge stage={r.status} label={r.status_label} testId={`rights-stage-${r.id}`} /></td>
  </tr>
);

export default function RightsRequests() {
  const auth = useAuth();
  const nav = useNavigate();
  const { meta, reload: reloadMeta } = useRightsMeta();
  const [dash, setDash] = useState(null);
  const [data, setData] = useState({ requests: [] });
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ q: "", request_type: ALL, subject_type: ALL, status: ALL, overdue: false });
  const [creating, setCreating] = useState(false);
  const [settings, setSettings] = useState(false);

  const load = useCallback(async () => {
    const p = { q: f.q || undefined, overdue: f.overdue || undefined };
    ["request_type", "subject_type", "status"].forEach((k) => { if (f[k] !== ALL) p[k] = f[k]; });
    try {
      const [l, d] = await Promise.all([api.get("/rights", { params: p }), api.get("/rights/dashboard")]);
      setData(l.data); setDash(d.data);
    } finally {
      setLoading(false);
    }
  }, [f]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));

  return (
    <AppShell testId="rights-requests-page">
      <PageTitle eyebrow="Operations workspace" title="Rights Requests"
                 subtitle="Access, correction, deletion, nominee and grievance requests from data principals, tracked from receipt to closure."
                 actions={<>
                   {auth.can("requests", "approve") && (
                     <Button variant="outline" onClick={() => setSettings(true)} data-testid="rights-settings-button">
                       <Settings2 className="w-4 h-4 mr-1" /> Verification settings
                     </Button>
                   )}
                   {auth.can("requests", "create") && (
                     <Button onClick={() => setCreating(true)} className="bg-[#008080] hover:bg-[#008080]/90" data-testid="rights-new-button">
                       <Plus className="w-4 h-4 mr-1" /> Log request
                     </Button>
                   )}
                 </>} />

      <RightsKpis d={dash} />
      <StagePipeline d={dash} active={f.status === ALL ? null : f.status} onPick={(s) => set("status")(s || ALL)} />

      <div className="flex items-center gap-2 flex-wrap bg-white border border-slate-200 rounded-xl p-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={f.q} onChange={(e) => set("q")(e.target.value)} placeholder="Ref, name, person key" className="h-9 pl-8 w-[230px] text-xs"
                 data-testid="rights-search-input" />
        </div>
        {meta && <>
          <Filter value={f.request_type} onChange={set("request_type")} placeholder="All request types"
                  options={Object.entries(meta.request_types)} testId="rights-filter-type" />
          <Filter value={f.subject_type} onChange={set("subject_type")} placeholder="All subjects"
                  options={Object.entries(meta.subject_types)} testId="rights-filter-subject" />
          <Filter value={f.status} onChange={set("status")} placeholder="All stages"
                  options={[["OPEN", "All open"], ...Object.entries(meta.stage_labels)]} testId="rights-filter-stage" />
        </>}
        <label className="flex items-center gap-2 text-xs text-slate-600 ml-2">
          <Switch checked={f.overdue} onCheckedChange={set("overdue")} data-testid="rights-filter-overdue" /> Overdue only
        </label>
      </div>

      {!loading && data.requests.length === 0 ? (
        <EmptyState icon={Scale} title="No rights requests match" hint="Log a request or clear the filters." testId="rights-empty-state" />
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm" data-testid="rights-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
                {["Ref", "Type", "Subject", "Received", "Target", "Owner", "Identity", "Stage"].map((h) => (
                  <th key={h} className="px-4 py-3 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.requests.map((r) => <Row key={r.id} r={r} onOpen={() => nav(`/rights/${r.id}`)} />)}
            </tbody>
          </table>
        </div>
      )}

      <NewRequestDialog open={creating} onOpenChange={setCreating} meta={meta} />
      <RightsSettingsDialog open={settings} onOpenChange={setSettings} meta={meta}
                            onSaved={reloadMeta} />
    </AppShell>
  );
}
